import iframemanager from '@orestbida/iframemanager';
import '@orestbida/iframemanager/dist/iframemanager.css';
import * as CookieConsent from 'vanilla-cookieconsent';

// All blocked embeds are gated by the "functional" (external media) category.
export const EMBED_CATEGORY = 'functional';

type Manager = ReturnType<typeof iframemanager>;
let manager: Manager | null = null;

// The published iframemanager build is an IIFE that registers
// `window.iframemanager`; it has no real default export, so the bundled default
// import may not be callable. Resolve the factory from either source.
function iframemanagerFactory(): () => Manager {
  if (typeof iframemanager === 'function') {
    return iframemanager as unknown as () => Manager;
  }
  const w = window as unknown as { iframemanager?: () => Manager };
  if (typeof w.iframemanager === 'function') {
    return w.iframemanager;
  }
  throw new Error('iframemanager is not available');
}

interface Texts {
  notice: string;
  loadBtn: string;
  loadAllBtn: string;
}

const TEXTS: Record<'de' | 'en', (provider: string) => Texts> = {
  de: (p) => ({
    notice: `Hier werden externe Inhalte von ${p} geladen. Dabei können Daten an ${p} übertragen werden.`,
    loadBtn: 'Inhalt laden',
    loadAllBtn: `${p} immer laden`,
  }),
  en: (p) => ({
    notice: `This will load external content from ${p}. Data may be transmitted to ${p}.`,
    loadBtn: 'Load content',
    loadAllBtn: `Always load ${p}`,
  }),
};

function service(lang: 'de' | 'en', provider: string, embedUrl: string, thumbnailUrl?: string) {
  const t = TEXTS[lang](provider);
  return {
    embedUrl,
    thumbnailUrl,
    iframe: {
      allow: 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen;',
    },
    languages: {
      [lang]: { notice: t.notice, loadBtn: t.loadBtn, loadAllBtn: t.loadAllBtn },
    },
  };
}

/**
 * Wire up iframemanager (MIT, by the CookieConsent author). The WordPress side
 * rewrites raw provider iframes into iframemanager markup
 * (`<div data-service="…" data-id="…">`); this loads/blocks them per consent.
 *
 * @param hooks.acceptCategory persists "functional" consent when a user clicks a
 *        placeholder's load button (so it survives reloads and updates GTM).
 */
export function setupContentBlocker(lang: 'de' | 'en', hooks: { acceptCategory: () => void }): void {
  if (manager) return;
  manager = iframemanagerFactory()();
  manager.run({
    currLang: lang,
    services: {
      youtube: service(lang, 'YouTube', 'https://www.youtube-nocookie.com/embed/{data-id}', 'https://i.ytimg.com/vi/{data-id}/hqdefault.jpg'),
      vimeo: service(lang, 'Vimeo', 'https://player.vimeo.com/video/{data-id}'),
      // Maps & Facebook embeds carry a full URL in data-id (no simple ID).
      maps: service(lang, 'Google Maps', '{data-id}'),
      facebook: service(lang, 'Facebook', '{data-id}'),
    },
    onChange: ({ eventSource }) => {
      // A click on a placeholder's load button should persist functional consent.
      if (eventSource && eventSource.type === 'click') {
        hooks.acceptCategory();
      }
    },
  });
}

/** Load or block all managed embeds based on the functional category state. */
export function syncContentBlocker(acceptedFunctional: boolean): void {
  if (!manager) return;
  if (acceptedFunctional) {
    manager.acceptService('all');
  } else {
    manager.rejectService('all');
  }
}

// --- Lazy-embed blocker -----------------------------------------------------
// Page builders (Elementor, UAEL) don't output raw <iframe>; they lazy-load the
// embed via their own JS from a data-src attribute. iframemanager/the server
// iframe rewrite never see those, so we block them here: neutralise the data-src
// (so the widget's JS can't load it) and overlay a placeholder until consent.

const EMBED_PROVIDERS: Array<{ match: string; name: string }> = [
  { match: 'youtube.com/embed', name: 'YouTube' },
  { match: 'youtube-nocookie.com', name: 'YouTube' },
  { match: 'youtu.be', name: 'YouTube' },
  { match: 'player.vimeo', name: 'Vimeo' },
  { match: 'google.com/maps', name: 'Google Maps' },
  { match: '/maps/embed', name: 'Google Maps' },
  { match: 'facebook.com/plugins', name: 'Facebook' },
];
const LAZY_SELECTOR = EMBED_PROVIDERS.map((p) => `[data-src*="${p.match}"]`).join(',');

const LAZY_TEXTS = {
  de: (p: string) => ({
    notice: `Externer Inhalt von ${p} ist blockiert. Zum Laden bitte zustimmen — dabei können Daten an ${p} übertragen werden.`,
    btn: 'Inhalt laden',
  }),
  en: (p: string) => ({
    notice: `External content from ${p} is blocked. Accept to load — data may be sent to ${p}.`,
    btn: 'Load content',
  }),
};

let lazyGated = false;

/** Block lazy-loaded (data-src) provider embeds while consent is missing. */
export function setupLazyEmbedBlocker(lang: 'de' | 'en'): void {
  if (CookieConsent.acceptedCategory(EMBED_CATEGORY)) {
    return; // consented -> leave the widget's own lazy loader intact
  }
  document.querySelectorAll<HTMLElement>(LAZY_SELECTOR).forEach((el) => {
    if (el.getAttribute('data-occ-lazy') === '1') return;
    const src = el.getAttribute('data-src') || '';
    const provider = (EMBED_PROVIDERS.find((p) => src.indexOf(p.match) !== -1) || { name: 'Drittanbieter' }).name;
    el.setAttribute('data-occ-lazy', '1');
    el.removeAttribute('data-src'); // neutralise the widget's lazy loader
    lazyGated = true;

    const container =
      (el.closest('.uael-video-inner-wrap, .uael-video__outer-wrap') as HTMLElement | null) || el;
    if (!container.style.position) container.style.position = 'relative';
    if (!container.style.minHeight) container.style.minHeight = '180px';

    const t = LAZY_TEXTS[lang](provider);
    const ph = document.createElement('div');
    ph.style.cssText =
      'position:absolute;inset:0;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:.8em;padding:1em;text-align:center;background:#0b1016;color:#fff;font-size:.9em;line-height:1.5;z-index:2';
    const p = document.createElement('p');
    p.textContent = t.notice;
    p.style.cssText = 'margin:0;max-width:32em';
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.textContent = t.btn;
    btn.style.cssText =
      'cursor:pointer;padding:.55em 1.2em;border:none;border-radius:.35em;background:var(--cc-btn-primary-bg,#2271b1);color:#fff;font-weight:600';
    btn.addEventListener('click', () => {
      CookieConsent.acceptCategory(EMBED_CATEGORY);
      window.location.reload();
    });
    ph.appendChild(p);
    ph.appendChild(btn);
    container.appendChild(ph);
  });
}

/** Reload once if lazy embeds were gated and functional consent is now given. */
export function lazyEmbedOnConsentChange(): void {
  if (lazyGated && CookieConsent.acceptedCategory(EMBED_CATEGORY)) {
    window.location.reload();
  }
}
